import {
  INVALID_PROJECT_TEXT_MESSAGE,
  INVALID_NAME_LENGTH_MESSAGE,
  INVALID_PROJECT_NUMBER_MESSAGE,
  INVALID_PROJECT_LENGTH_MESSAGE
} from './constants';
import {
  PROJECTS_SORT_SEARCH,
  CARD_VIEW_TYPE,
  ANALYTICS_ACTION_SORT,
  ANALYTICS_ACTION_SEARCH,
  ANALYTICS_ACTION_MIXED,
  ANALYTICS_CLEAR_FILTERS,
  ANALYTICS_PROJECT_NAME_A_TO_Z,
  ANALYTICS_PROJECT_NAME_Z_TO_A,
  ANALYTICS_PROJECT_NUMBER_A_TO_Z,
  ANALYTICS_PROJECT_NUMBER_Z_TO_A,
  ANALYTICS_PROJECT_STATUS_ACTIVE_FIRST,
  ANALYTICS_PROJECT_STATUS_ARCHIVED_FIRST,
  ANALYTICS_LAST_MODIFIED_MOST_RECENT,
  ANALYTICS_LAST_MODIFIED_OLDEST_FIRST,
  ANALYTICS_PROJECT_NAME_USER_ENTRY,
  ANALYTICS_PROJECT_NUMBER_USER_ENTRY,
  ANALYTICS_PROJECT_STATUS_ACTIVE,
  ANALYTICS_PROJECT_STATUS_ARCHIVED,
  ANALYTICS_NOT_APPLICABLE
} from './components/project-summary-helper';

export const dateModifiedMessage = (modifyDateTime) => {
  const currentDateTime = new Date();
  const parsedCurrentDateTime = Date.parse(currentDateTime);
  const currentDate = new Date(currentDateTime.toLocaleDateString('en-US'));

  const lastModifiedDateTime = new Date(modifyDateTime);
  const lastModifiedLocalDateTime = new Date(lastModifiedDateTime.toLocaleString('en-US'));
  const lastModifiedDate = new Date(lastModifiedDateTime.toLocaleDateString('en-US'));
  const parsedLastModifiedDateTime = Date.parse(lastModifiedLocalDateTime);

  const secondsSinceLastUpdate = Math.floor((parsedCurrentDateTime - parsedLastModifiedDateTime) / 1000);
  const daysSinceLastUpdate = Math.floor((Math.abs(lastModifiedDate - currentDate)) / (1000 * 60 * 60 * 24));

  // Same day updates
  if (daysSinceLastUpdate < 1) {
    // Just now (less than a minute)
    if (secondsSinceLastUpdate <= 60) {
      return 'Just now';
    }

    // Minutes
    if (secondsSinceLastUpdate < 3600) {
      const minutes = Math.floor(secondsSinceLastUpdate / 60);
      return minutes === 1 ? `${minutes} minute ago` : `${minutes} minutes ago`;
    }

    // Hours
    const hours = Math.floor(secondsSinceLastUpdate / 3600);
    return hours === 1 ? `${hours} hour ago` : `${hours} hours ago`;
  }

  // Yesterday
  if (daysSinceLastUpdate === 1) {
    return 'Yesterday';
  }

  // Recent days (2-6 days ago)
  if (daysSinceLastUpdate <= 6) {
    return `${daysSinceLastUpdate} days ago`;
  }

  // More than a week ago
  return lastModifiedDateTime.toLocaleDateString();
};

export const validateProjectName = (value) => {
  // Don't validate empty values
  if (!value || value.trim() === '') {
    return { isError: false, errorMessage: '' };
  }
  const validCharacters = /^(?=.{0,80}$)[ [\]{}|a-z|A-Z|!@#$%^&*()0-9\-_+,.?"':;\\]+$/;
  let isValid = validCharacters.test(value);
  const charLimit = value?.length > 80;
  let errorMessage = '';
  if (!isValid) {
    if (charLimit) {
      errorMessage = INVALID_NAME_LENGTH_MESSAGE;
    } else {
      errorMessage = INVALID_PROJECT_TEXT_MESSAGE;
    }
  }
  return { isError: !isValid, errorMessage };
};

export const validateProjectNumber = (value) => {
  // Don't validate empty values
  if (!value || value.trim() === '') {
    return { isError: false, errorMessage: '' };
  }

  const validCharacters = /^[a-zA-Z0-9]+$/;
  let isValid = validCharacters.test(value);
  let errorMessage = '';
  if (!isValid) {
    errorMessage = INVALID_PROJECT_NUMBER_MESSAGE;
  }
  return { isError: !isValid, errorMessage };
};

export const validateProjectNumberLength = (value) => {
  // Don't validate empty values
  if (!value || value.trim() === '') {
    return { isError: false, errorMessage: '' };
  }

  const charCountCheckFailed = value?.length !== 10;
  let isValid = !charCountCheckFailed;
  let errorMessage = '';
  if (!isValid) {
    errorMessage = INVALID_PROJECT_LENGTH_MESSAGE;
  }
  return { isError: !isValid, errorMessage };
};

/**
 * Utility function to check if any filter criteria are currently applied
 * @param {Object} filterCriteria - The filter criteria object
 * @param {string} filterCriteria.projectName - Project name filter
 * @param {string} filterCriteria.projectNumber - Project number filter
 * @param {string} filterCriteria.projectStatus - Project status filter
 * @returns {boolean} True if any filters are active, false otherwise
 */
export const hasActiveFilters = (filterCriteria) => {
  const hasProjectName = filterCriteria?.projectName
    && typeof filterCriteria.projectName === 'string'
    && filterCriteria.projectName.trim();

  const hasProjectNumber = filterCriteria?.projectNumber
    && typeof filterCriteria.projectNumber === 'string'
    && filterCriteria.projectNumber.trim();

  const hasProjectStatus = filterCriteria?.projectStatus
    && typeof filterCriteria.projectStatus === 'string'
    && filterCriteria.projectStatus.trim();

  return Boolean(hasProjectName || hasProjectNumber || hasProjectStatus);
};

export const triggerAnalytics = (event, payload) => {
  LIFE_CYCLE_EVENT_BUS.trigger(event, payload);
};

export const triggerLifeCycleAnalytics = (event, payload) => {
  LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(event, payload);
};

/**
 * Helper function to determine the action type based on sorting and filtering state
 */
const getActionType = (hasSorting, hasFiltering) => {
  if (hasSorting && hasFiltering) {
    return ANALYTICS_ACTION_MIXED;
  }
  if (hasSorting) {
    return ANALYTICS_ACTION_SORT;
  }
  return ANALYTICS_ACTION_SEARCH;
};

/**
 * Helper function to get the search format for sorting
 */
const getSortSearchFormat = (sortCriteria) => {
  const sortBy = sortCriteria[0].sortBy;
  const sortOrder = sortCriteria[0].sortOrder;

  const sortTermMap = {
    PROJECT_NAME: {
      ASC: ANALYTICS_PROJECT_NAME_A_TO_Z,
      DSC: ANALYTICS_PROJECT_NAME_Z_TO_A
    },
    PROJECT_ID: {
      ASC: ANALYTICS_PROJECT_NUMBER_A_TO_Z,
      DSC: ANALYTICS_PROJECT_NUMBER_Z_TO_A
    },
    STATUS: {
      ASC: ANALYTICS_PROJECT_STATUS_ACTIVE_FIRST,
      DSC: ANALYTICS_PROJECT_STATUS_ARCHIVED_FIRST
    },
    LAST_MODIFIED: {
      ASC: ANALYTICS_LAST_MODIFIED_MOST_RECENT,
      DSC: ANALYTICS_LAST_MODIFIED_OLDEST_FIRST
    }
  };

  return sortTermMap[sortBy]?.[sortOrder] || ANALYTICS_NOT_APPLICABLE;
};

/**
 * Helper function to get the search value for filtering
 */
const getFilterSearchValue = (filterCriteria) => {
  const searchValueParts = [];

  if (filterCriteria.projectName && filterCriteria.projectName.trim()) {
    searchValueParts.push(ANALYTICS_PROJECT_NAME_USER_ENTRY);
  }

  if (filterCriteria.projectNumber && filterCriteria.projectNumber.trim()) {
    searchValueParts.push(ANALYTICS_PROJECT_NUMBER_USER_ENTRY);
  }

  if (filterCriteria.projectStatus && filterCriteria.projectStatus.trim()) {
    const isActiveStatus = filterCriteria.projectStatus.toLowerCase() === 'active';
    const statusValue = isActiveStatus ? ANALYTICS_PROJECT_STATUS_ACTIVE : ANALYTICS_PROJECT_STATUS_ARCHIVED;
    searchValueParts.push(statusValue);
  }

  return searchValueParts.length > 0 ? searchValueParts.join(', ') : ANALYTICS_NOT_APPLICABLE;
};

export const sortAndFilterAnalytics = ({ sortCriteria, filterCriteria, viewType = CARD_VIEW_TYPE }) => {
  const hasSorting = sortCriteria && sortCriteria.length > 0;
  const hasFiltering = hasActiveFilters(filterCriteria);

  // Early return if neither sorting nor filtering
  if (!hasSorting && !hasFiltering) {
    return;
  }

  // Determine action type based on what's active
  const actionType = getActionType(hasSorting, hasFiltering);

  // Format the filterType value according to spec
  const filterType = `projects: ${viewType}: ${actionType}`;

  // Format search term (sort field and order)
  const searchFormat = hasSorting ? getSortSearchFormat(sortCriteria) : ANALYTICS_NOT_APPLICABLE;

  // Format search by (filter field and value)
  const searchValue = hasFiltering ? getFilterSearchValue(filterCriteria) : ANALYTICS_NOT_APPLICABLE;

  // Trigger analytics with new format
  triggerAnalytics(PROJECTS_SORT_SEARCH, {
    filterType,
    searchFormat,
    searchValue
  });
};

export const clearFiltersAnalytics = ({ viewType = CARD_VIEW_TYPE, clearType = ANALYTICS_CLEAR_FILTERS }) => {
  const filterType = `projects: ${viewType}: ${clearType}`;

  triggerAnalytics(PROJECTS_SORT_SEARCH, {
    filterType
  });

};